import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import AdminSidebar from '../components/AdminSidebar';
import DashboardHeader from '../components/DashboardHeader';
import { useDb } from '../utils/DbContext';
import './Dashboard.css';

function formatRows(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  return x.toLocaleString();
}

/** `pg_total_relation_size`: heap + TOAST + indexes. */
function formatBytes(n) {
  const x = Number(n);
  if (!Number.isFinite(x) || x < 0) return '—';
  if (x === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let u = 0;
  let v = x;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u += 1;
  }
  const digits = u === 0 ? 0 : v >= 100 ? 0 : v >= 10 ? 1 : 2;
  return `${v.toFixed(digits)} ${units[u]}`;
}

function DatabaseOverview() {
  const { dbName } = useDb();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/data/overview');
      setPayload(res.data);
    } catch (e) {
      setPayload(null);
      setError(e.response?.data?.error || e.message || 'Failed to load overview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, dbName]);

  return (
    <div className="dashboard">
      <DashboardHeader title="Database overview" />

      <div className="dashboard-layout">
        <AdminSidebar />
        <div className="dashboard-content overview-content">
          {error && <div className="message error">{error}</div>}
          {loading && <p className="overview-meta">Loading…</p>}

          {!loading && payload && (
            <>
              <p className="overview-meta">
                Logical DB: <code>{payload.logicalDb}</code>
                {payload.pgDatabase ? (
                  <>
                    {' · '}
                    PostgreSQL database: <code>{payload.pgDatabase}</code>
                  </>
                ) : null}
                <br />
                Row counts are <strong>approximate</strong> (from <code>pg_stat</code> live tuple
                estimates; not <code>COUNT(*)</code>). Table size is{' '}
                <code>pg_total_relation_size</code> (heap, TOAST, and indexes).
              </p>

              <div className="overview-actions">
                <Link to="/dashboard" className="submit-button overview-cta">
                  Open table browser
                </Link>
              </div>

              {payload.schemas.map(({ schema, tables, totalRowEstimate, totalBytes }) => (
                <div key={schema} className="form-section overview-schema-block">
                  <h2 className="section-title">
                    Schema <code>{schema}</code>
                  </h2>
                  <p className="overview-meta">
                    {tables.length} table{tables.length === 1 ? '' : 's'} · Σ rows (approx.):{' '}
                    {formatRows(totalRowEstimate)}
                    {' · '}
                    Σ size: {formatBytes(totalBytes ?? 0)}
                  </p>
                  {tables.length === 0 ? (
                    <p className="overview-meta">No base tables in this schema.</p>
                  ) : (
                    <div className="overview-table-wrap">
                      <table className="overview-table">
                        <thead>
                          <tr>
                            <th>Table</th>
                            <th className="overview-col-numeric">Rows (approx.)</th>
                            <th className="overview-col-numeric">Total size</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tables.map((t) => (
                            <tr key={`${schema}.${t.table}`}>
                              <td>
                                <code>{t.table}</code>
                              </td>
                              <td className="overview-col-numeric">{formatRows(t.rowEstimate)}</td>
                              <td className="overview-col-numeric">
                                {formatBytes(t.totalBytes ?? 0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DatabaseOverview;
